import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {CustomActionField, type ICustomAction} from './customActionsSchema';

export const CUSTOM_ACTIONS_SORTABLE_FIELDS = [CustomActionField.name] as const;

export const CustomActionsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(CUSTOM_ACTIONS_SORTABLE_FIELDS),
});

export type CustomActionsSort = z.infer<typeof CustomActionsSortSchema>;

export const CustomActionsSortableField = z.enum(
	CUSTOM_ACTIONS_SORTABLE_FIELDS,
).enum;

export const CUSTOM_ACTIONS_DEFAULT_SORT_FIELD =
	CustomActionsSortableField.name;

export const CUSTOM_ACTIONS_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getCustomActionsDefaultSort = (
	sort: Partial<CustomActionsSort> = {},
): CustomActionsSort => ({
	field: getCustomActionsSortField({sortField: sort.field}),
	order: getCustomActionsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getCustomActionsSortField = (
	params: Omit<
		GetSortFieldParams<(typeof CUSTOM_ACTIONS_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: CUSTOM_ACTIONS_SORTABLE_FIELDS,
		defaultSortField: CUSTOM_ACTIONS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getCustomActionsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: CUSTOM_ACTIONS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const editCustomActionsEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			ICustomAction,
			(typeof CUSTOM_ACTIONS_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: CUSTOM_ACTIONS_SORTABLE_FIELDS,
	});
